import React, { useState, useEffect } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'

import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'
import { useAuth } from '../hooks/useAuth'
import { AppPath } from '../enums/AppPath'

import EstimateEdit from './EstimateEdit'
import ConsignmentEdit from './ConsignmentEdit'
import ReservationEdit from './ReservationEdit'
import VehicleCard from './VehicleCard'
import { ClientCard } from './ClientCard'
import SettlementEdit from './SettlementEdit'
import SalenoteCrea from './SalenoteCrea'
import CreditCreaPage from './CreditCreaPage'
import PurchaseEdit from './PurchaseEdit'

const VehCliSelection: React.FC = () => {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const [client, setClient] = useState<Client | null>(null)
  const { errorClient, fetchClient } = useClientFetcher()
  const { docuType } = useParams<{ docuType: string }>()
  const { selectedPair, setSelectedPair } = useAuth()
  const [searchParams] = useSearchParams()

  const paramVehicleId = searchParams.get('vehicleId')
  const paramClientId = searchParams.get('clientId')

  const vehicleId = paramVehicleId || selectedPair?.vehicleId || ''
  const clientId = paramClientId || selectedPair?.clientId || ''

  useEffect(() => {
    if (
      (paramVehicleId && paramVehicleId !== selectedPair?.vehicleId) ||
      (paramClientId && paramClientId !== selectedPair?.clientId)
    ) {
      setSelectedPair({
        vehicleId: paramVehicleId || selectedPair?.vehicleId,
        clientId: paramClientId || selectedPair?.clientId,
        consignment: selectedPair?.consignment,
      })
    }
  }, [paramVehicleId, paramClientId])
  const hrefVeh = `/vehicles/${docuType}`
  const hrefCli = `/clients/${docuType}`

  useEffect(() => {
    if (vehicleId === '') {
      return
    }

    const fetchDataAsync = async () => {
      const vehicle = await fetchVehicle(vehicleId)
      setVehicle(vehicle)
    }

    fetchDataAsync()
  }, [vehicleId])

  useEffect(() => {
    if (clientId === '') {
      return
    }

    const fetchDataAsync = async () => {
      const client = await fetchClient(clientId)
      setClient(client)
    }

    fetchDataAsync()
  }, [clientId])

  let content

  if (client && vehicle) {
    const consignment = selectedPair?.consignment ?? null

    switch (docuType) {
      case AppPath.Estimates:
        content = <EstimateEdit vehicle={vehicle} client={client} />
        break

      case AppPath.Consignments:
        content = <ConsignmentEdit />
        break

      case AppPath.Reservations:
        content = <ReservationEdit vehicle={vehicle} client={client} />
        break

      case AppPath.SaleNotes:
        content = <SalenoteCrea vehicle={vehicle} client={client} />
        break

      case AppPath.Settlements:
        if (consignment) {
          content = <SettlementEdit consignment={consignment} vehicle={vehicle} />
        }
        break
      case AppPath.Purchases:
        content = <PurchaseEdit vehicle={vehicle} client={client} />
        break

      case AppPath.Loans:
        content = <CreditCreaPage vehicle={vehicle} client={client} />
        break
      default:
        content = <div>NO IMPLEMENTADO:{docuType}</div>
    }
  } else {
    content = (
      <div className="flex items-center space-x-2 text-sm text-gray-600">
        <span className="text-xl">ℹ️</span>
        <span>Por favor asigna un vehículo y un cliente primero.</span>
      </div>
    )
  }

  if (errorVehicle) {
    return <div>Error: {errorVehicle.message}</div>
  }

  if (errorClient) {
    return <div>Error: {errorClient.message}</div>
  }

  const showVehicleClientCards = docuType !== AppPath.Settlements

  return (
    <div className="flex flex-col items-center space-y-2">
      {showVehicleClientCards && (
        <div className="flex space-x-4">
          <Link
            className="flex items-center justify-center p-4 border rounded-md cursor-pointer hover:bg-gray-100"
            to={hrefVeh}
          >
            <VehicleCard vehicle={vehicle} />
          </Link>
          <Link
            className="flex items-center justify-center p-4 border rounded-md cursor-pointer hover:bg-gray-100"
            to={hrefCli}
          >
            <ClientCard client={client} />
          </Link>
        </div>
      )}
      {content}
    </div>
  )
}

export default VehCliSelection
